// ── Study movies & key images ───────────────────────────────────────────────────
// Movies (MP4 cine loops) and key images derived from DICOM — generated in the
// browser (movieGen.ts) or attached by the owner. The blob lives in Firebase
// Storage under users/{uid}/profiles/{pid}/movies/{studyKey}/…; a Firestore doc in
// the `movies` subcollection links it to a study and carries the metadata.
import { collection, addDoc, getDocs, doc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { fbDb, fbStorage, fbAuth } from './firebase';
import type { StudyMovie } from './types';

const moviesCol = (ownerUid: string, pid: string) => collection(fbDb, 'users', ownerUid, 'profiles', pid, 'movies');
const safe = (s: string) => (s || 'study').replace(/[^A-Za-z0-9._-]+/g, '_').slice(0, 80);

/** Upload a blob to this profile's movies area; returns its Storage object path. */
export async function uploadMovieBlob(
  ownerUid: string, pid: string, studyKey: string, filename: string, blob: Blob,
): Promise<string> {
  const path = `users/${ownerUid}/profiles/${pid}/movies/${safe(studyKey)}/${filename}`;
  await uploadBytes(ref(fbStorage, path), blob, { contentType: blob.type || 'application/octet-stream' });
  return path;
}

/** A temporary download URL for a stored movie/image object. */
export const movieUrl = (storagePath: string): Promise<string> => getDownloadURL(ref(fbStorage, storagePath));

/** Create the Firestore link doc for a movie/key image (stamps the creator so the
 *  rules attribute it — same append-only/provenance model as clinical records). */
export async function addMovieDoc(ownerUid: string, pid: string, data: Omit<StudyMovie, 'id'>): Promise<string> {
  const u = fbAuth.currentUser;
  const ref2 = await addDoc(moviesCol(ownerUid, pid), {
    ...data,
    createdByUid: u?.uid ?? null,
    createdByEmail: (u?.email ?? '').toLowerCase(),
    createdByName: u?.displayName ?? '',
    createdAt: serverTimestamp(),
  });
  return ref2.id;
}

/** Every movie/key image on this profile (caller filters by studyKey). */
export async function listMovies(ownerUid: string, pid: string): Promise<StudyMovie[]> {
  const snap = await getDocs(moviesCol(ownerUid, pid));
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as StudyMovie[];
}

/** Delete a movie: its Firestore link + the Storage blob(s). Best-effort on blobs. */
export async function removeMovie(ownerUid: string, pid: string, m: StudyMovie): Promise<void> {
  if (m.id) await deleteDoc(doc(fbDb, 'users', ownerUid, 'profiles', pid, 'movies', m.id));
  for (const p of [m.storagePath, m.thumbPath]) {
    if (p) { try { await deleteObject(ref(fbStorage, p)); } catch { /* already gone / no perms */ } }
  }
}
